/**
 * portalService.js — Firma portalı (backlog #311-#321, 2026-10-11).
 *
 * Base: {JAVA_BACKEND_API_URL}/portal — FDS-Backend PortalController (#310, #314, #316, #317), artı
 * firma uyruğu (#312, FirmaController) ve pasaport (#314, PersonnelController) uçları.
 *
 * Portalın tek kaynağı GET /portal/context: seçili fuarda firmanın rolleri × uyruğu → arayüz setleri
 * (Yerli/Yabancı × Katılımcı/Dekoratör/Organizatör), her setin modül seviyeleri (NONE/READ/WRITE/MANAGE)
 * ve organizatör eylemleri. Menü, rotalar ve sayfalardaki düğmeler buna bakar; backend aynı kuralı
 * uçlarda da uygular (#315), yani gizlemek tek kapı değil.
 *
 * Mock yok: firma bilgisi alınamazsa sayfa HATA gösterir, hiçbir zaman test dekoratörünün (49858)
 * portalını değil (#311).
 */
import axios from 'axios';
import { JAVA_BACKEND_API_URL, getAuthHeader } from '../Components/Contexts/BackendConstants';

const BASE = `${JAVA_BACKEND_API_URL}/portal`;

function parseError(error) {
  const data = error.response?.data;
  const message =
    data?.message ||
    (typeof data === 'string' && data ? data : null) ||
    error.message ||
    'Bilinmeyen hata';
  const wrapped = new Error(message);
  wrapped.status = error.response?.status ?? null;
  wrapped.data = data;
  throw wrapped;
}

const get = (url, params) =>
  axios.get(url, { headers: getAuthHeader(), params }).then((r) => r.data).catch(parseError);
const put = (url, body) =>
  axios.put(url, body, { headers: getAuthHeader() }).then((r) => r.data).catch(parseError);
const del = (url, body) =>
  axios.delete(url, { headers: getAuthHeader(), data: body }).then((r) => r.data).catch(parseError);

/** Modül anahtarları — backend PortalConstant.Modul ile birebir. */
export const MODUL = {
  DASHBOARD: 'DASHBOARD',
  PERSONEL: 'PERSONEL',
  FIRMA_BELGELERI: 'FIRMA_BELGELERI',
  ISG_BELGELERI: 'ISG_BELGELERI',
  TASERON: 'TASERON',
  DEKORATOR_BILGISI: 'DEKORATOR_BILGISI',
  STANDLAR: 'STANDLAR',
  TALEPLER: 'TALEPLER',
  BORC: 'BORC',
  PROJELER: 'PROJELER',
  FIRMA_YONETIMI: 'FIRMA_YONETIMI',
  KROKI: 'KROKI',
  BELGE_GEREKSINIMLERI: 'BELGE_GEREKSINIMLERI',
  // #436 (2026-10-16): firmanın kendi profili (kimlik, iletişim, logo, fotoğraflar, yetkililer). WRITE'ı olmayan giriş düzenleme
  // düğmelerini görmez; backend zaten 403 verir.
  FIRMA_PROFILI: 'FIRMA_PROFILI',
  // #528 (2026-10-21): the 15th module — whether the company's Firma Kartı shows the "Event Catalogue Information" tab (#539; WAS "Fair Catalogue Information"). The BACKEND does not know it yet
  // (backend task #529): until it does, /portal/context carries no value and the tab shows as before (Components/Firma/fuarKatalogSekmesi.js).
  FUAR_KATALOG: 'FUAR_KATALOG',
};

/** Seviyeler, sıralı: büyük olan küçüğü içerir. */
export const SEVIYE = { NONE: 'NONE', READ: 'READ', WRITE: 'WRITE', MANAGE: 'MANAGE' };
const SIRA = ['NONE', 'READ', 'WRITE', 'MANAGE'];
export const enAz = (seviye, min) => SIRA.indexOf(seviye || 'NONE') >= SIRA.indexOf(min);

/** Organizatör eylemleri + fuar geneli görünüm — backend PortalConstant (#316). */
export const ORGANIZATOR_AYAR = {
  FUAR_GORUNUMU: 'FUAR_GORUNUMU',
  STAND_EKLE: 'STAND_EKLE',
  STAND_DUZENLE: 'STAND_DUZENLE',
  STAND_SIL: 'STAND_SIL',
  FIRMA_EKLE: 'FIRMA_EKLE',
  FIRMA_ESLE: 'FIRMA_ESLE',
  FIRMA_SIL: 'FIRMA_SIL',
  TASERON_EKLE: 'TASERON_EKLE',
  TALEP_EKLE: 'TALEP_EKLE',
  TALEP_SIL: 'TALEP_SIL',
  BORC_YONET: 'BORC_YONET',
  // #455 (2026-10-17): the organizer may enter its own payment link (the free-kW notice); off until SAKA turns it on for the company
  ODEME_BAGLANTISI_YONET: 'ODEME_BAGLANTISI_YONET',
};

/**
 * #446 (2026-10-17): the organizer's view of the OTHER companies' data is one tier per area (backend PortalConstant.Alan / Kademe), not one
 * fair-wide switch. NONE sees nothing, MONITOR (İzleme) reads lists and statuses, AUDIT (Denetim) is MONITOR plus the action flags of the area.
 * FUAR_GORUNUMU above is retired: the backend still reads it as "every NONE area becomes MONITOR" on a write, and no longer offers it.
 */
export const ORGANIZATOR_ALAN = {
  STANDLAR: 'STANDLAR',
  FIRMALAR: 'FIRMALAR',
  TALEPLER: 'TALEPLER',
  PROJELER: 'PROJELER',
  KISILER: 'KISILER',
  SALONLAR: 'SALONLAR',
};
export const ORGANIZATOR_ALANLAR = Object.values(ORGANIZATOR_ALAN);
export const KADEME = { NONE: 'NONE', MONITOR: 'MONITOR', AUDIT: 'AUDIT' };

// ── Bağlam (#310 / #311) ─────────────────────────────────────────────────────────────────

/**
 * GET /portal/context → { staff, onizleme, firmalar, firma: {firmaId, firmaName, firmaNation, yabanci},
 * fuarId, izinler, kullaniciRolleri: [{kod, ad, kaynak}], rolKaynagi, roller,
 * arayuzler: [{kod, ad, rol, yabanci, moduller, gerekceler: {MODUL: {seviye, gerekce, kaynak, tavan, rol, kisit, kisitGerekcesi}},
 *              eylemler: [{kod, izinli, gerekce, modul, gerekenSeviye}], fuarGorunumu?}],
 * aktifArayuz, uyari? }. Personel `firmaId` vermek zorunda (önizleme); #417: `kullaniciId` ile o girişin rolleriyle önizler.
 * (#418: kullaniciRolleri.ad ve gerekceler istek dilinde; backend Accept-Language'e bakar.)
 */
// WAS (until #417): export function getPortalContext({ fuarId, arayuz, firmaId } = {}) { return get(..., { fuarId, arayuz, firmaId }); }
export function getPortalContext({ fuarId, arayuz, firmaId, kullaniciId } = {}) {
  return get(`${BASE}/context`, {
    fuarId: fuarId || undefined, arayuz: arayuz || undefined, firmaId: firmaId || undefined, kullaniciId: kullaniciId || undefined,
  });
}

/** GET /portal/matris — temel matris ve her modülü açan portal yetkisi. */
export function getPortalMatris() {
  return get(`${BASE}/matris`);
}

// ── Belge gereksinimleri (#314 / #318) ───────────────────────────────────────────────────

/** GET /portal/belge-gereksinimleri?fuarId= — altı set, her belge GEREKLI / ISTEGE_BAGLI / UYGULANMAZ. */
export function getBelgeGereksinimleri(fuarId) {
  return get(`${BASE}/belge-gereksinimleri`, { fuarId });
}

// ── Organizatör ayarları (#316, SAKA personeli yazar) ─────────────────────────────────────

export function getOrganizatorAyar(firmaId) {
  return get(`${BASE}/organizator-ayar/${firmaId}`);
}

/**
 * ayarlar: { STAND_EKLE: true, ... } — istenen alt küme. Yalnızca personel.
 * #446: alanlar: { STANDLAR: 'MONITOR', PROJELER: 'NONE', ... } — bir alanın kademesi (NONE / MONITOR / AUDIT); ikisi de alt küme olabilir.
 * WAS (until #446): setOrganizatorAyar(firmaId, ayarlar, not) — the one FUAR_GORUNUMU switch was part of ayarlar.
 */
export function setOrganizatorAyar(firmaId, ayarlar, not, alanlar) {
  const body = { not: not || null };
  if (ayarlar && Object.keys(ayarlar).length > 0) body.ayarlar = ayarlar;
  if (alanlar && Object.keys(alanlar).length > 0) body.alanlar = alanlar;
  return put(`${BASE}/organizator-ayar/${firmaId}`, body);
}

// ── Organizatör: kendi fuarlarının tamamı (#316 / #317 / #321) ────────────────────────────
// Her uç fuar geneli görünüm ister (organizatör + SAKA'nın açtığı FUAR_GORUNUMU, ya da personel).

export const getOrganizatorFuarlari = () => get(`${BASE}/organizator/fuarlar`);
export const getOrganizatorStandlar = (fuarId, params) => get(`${BASE}/organizator/${fuarId}/standlar`, params);
export const getOrganizatorFirmalar = (fuarId, params) => get(`${BASE}/organizator/${fuarId}/firmalar`, params);
export const getOrganizatorTalepler = (fuarId, params) => get(`${BASE}/organizator/${fuarId}/talepler`, params);
/**
 * #500 (backend #499): the stands whose Elektrik request is above the fair's free kW, the largest excess first, with the fair's rule and the totals.
 * params: search, salonId, degerlendirilemeyen=true (list the stands that cannot be judged instead), page (0-based), size (<= 100). Same area guard as the request list (TALEPLER).
 */
export const getOrganizatorSerbestKwAsimi = (fuarId, params) => get(`${BASE}/organizator/${fuarId}/talepler/serbest-kw-asimi`, params);
export const getOrganizatorProjeler = (fuarId, params) => get(`${BASE}/organizator/${fuarId}/projeler`, params);
export const getOrganizatorPersonel = (fuarId, params) => get(`${BASE}/organizator/${fuarId}/personel`, params);
/** #448: "Kişiler" = the same statuses-only list under its new name (KISILER tier); /personel is the old name. */
export const getOrganizatorKisiler = (fuarId, params) => get(`${BASE}/organizator/${fuarId}/kisiler`, params);
/**
 * #451: one project, read-only, for an organizer (PROJELER tier): the details and the LIST of files, no stored file names, no way to a file.
 * The same endpoint the company and staff pages read; the backend answers an organizer with the stripped body.
 */
export const getOrganizatorProjeDetay = (projeId) => get(`${JAVA_BACKEND_API_URL}/projects/${projeId}/detail`);
/** #322: fuarın AKTİF salonları (salon_to_fuar) — [{salonId, salonName, alanPlaniDurum, standSayisi}], salt okunur. */
export const getOrganizatorSalonlar = (fuarId) => get(`${BASE}/organizator/${fuarId}/salonlar`);

/**
 * #455 / #456: the organizer's PAYMENT LINKS, the notice an exhibitor sees when it asks for more kW than the fair gives free.
 * GET /portal/odeme-baglantisi/{firmaId} -> { firmaId, baglantilar: [{ id, fuarId (0 = every fair), url, host, etiket, aktif, tarih, userId, userAdSoyad }] }
 * Staff, or a login of that company.
 */
export const getOdemeBaglantisi = (firmaId) => get(`${BASE}/odeme-baglantisi/${firmaId}`);

/**
 * PUT /portal/odeme-baglantisi/{firmaId}, body { fuarId, url, etiket?, aktif? } -> the same list as the GET. Staff: any company, any fair, or fuarId 0 = every fair.
 * An organizer login: only its own fair (fuarId > 0) and only where SAKA turned the action ODEME_BAGLANTISI_YONET on for the company; the backend refuses the rest.
 * ⚠️ The link goes to every exhibitor: the backend accepts https and a domain name only.
 */
export const setOdemeBaglantisi = (firmaId, govde) => put(`${BASE}/odeme-baglantisi/${firmaId}`, govde);

/** #317: Hizmet / İş İzni / Ekipman talebini iptal et (silinmez: durum 3 İptal). reason zorunlu. */
export function talepIptalEt(standId, requestId, reason) {
  return del(`${JAVA_BACKEND_API_URL}/stands/talepler/${standId}/talepler/${requestId}`, { reason });
}

/** #317: bir Teknik kategoriyi (elektrik/su/hava/internet) geri çek. */
export function teknikTalepGeriCek(standId, categoryKey) {
  return del(`${JAVA_BACKEND_API_URL}/stands/talepler/${standId}/teknik/${categoryKey}`);
}

/** #317: borç kategorisi — categoryKey alan|teknik|ekipman|hizmet, status yok|bekliyor|odendi. */
export function setStandBorc(standId, categoryKey, status, amount) {
  return put(`${JAVA_BACKEND_API_URL}/standTalep/borc/${standId}/${categoryKey}`, { status, amount: amount ?? null });
}

// ── Firma uyruğu (#312 / #313) ───────────────────────────────────────────────────────────

/** GET /firma/{id}/yabanci — personele ayrıca not / ayarlayan / tarih. */
export function getFirmaYabanci(firmaId) {
  return get(`${JAVA_BACKEND_API_URL}/firma/${firmaId}/yabanci`);
}

/** PUT /firma/{id}/yabanci — yalnızca personel. body: {yabanci, not?, taxNumber?, taxNumberLabel?} */
export function setFirmaYabanci(firmaId, body) {
  return put(`${JAVA_BACKEND_API_URL}/firma/${firmaId}/yabanci`, body);
}

/** GET /firma/uyruk-listesi — yalnızca personel. params: {firmaNation, turkiye, organizator, search, page, size} */
export function getUyrukListesi(params) {
  return get(`${JAVA_BACKEND_API_URL}/firma/uyruk-listesi`, params);
}

// ── Pasaport ve kimlik doğrulama (#314) ──────────────────────────────────────────────────

/** GET /personnel/{id}/kimlik-dogrulama — { durum, durumAdi, dogrulandi, belgeTuru, pasaportNo, pasaportUlkeId, tcVar, ... } */
export function getKimlikDogrulama(personId) {
  return get(`${JAVA_BACKEND_API_URL}/personnel/${personId}/kimlik-dogrulama`);
}

/** GET /portal/ulkeler — [{countryId, countryName}] (pasaportun ülkesi için). */
export function getUlkeler() {
  return get(`${BASE}/ulkeler`);
}

/** PUT /personnel/{id}/pasaport — { pasaportNo, ulkeId }; boş numara siler. */
export function setPasaport(personId, pasaportNo, ulkeId) {
  return put(`${JAVA_BACKEND_API_URL}/personnel/${personId}/pasaport`, { pasaportNo, ulkeId: ulkeId || null });
}
